import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Download } from 'lucide-react';
import type { DocumentResponse } from '@timesheet/schemas';
import { Button } from '../ui/button';
import { formatDate, formatSize, iconForMime, periodLabel } from './documents.utils';

interface DocumentRowProps {
  doc: DocumentResponse;
  /** Whether the target person is worth showing (hidden when everyone in the list is the viewer). */
  showPerson: boolean;
  onOpen: (doc: DocumentResponse) => void;
  onDownload: (doc: DocumentResponse) => void;
}

/** One line of the list. No inline actions: the row opens the detail popup. */
export const DocumentRow: React.FC<DocumentRowProps> = ({ doc, showPerson, onOpen, onDownload }) => {
  const { t, i18n } = useTranslation();
  const Icon = iconForMime(doc.mimeType);
  const primary = periodLabel(doc, t, i18n.language);
  const secondary = [
    showPerson && doc.targetUser ? doc.targetUser.name : null,
    formatDate(doc.createdAt, i18n.language),
    formatSize(doc.sizeBytes),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(doc)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen(doc);
        }
      }}
      className="flex w-full cursor-pointer items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      data-testid={`document-row-${doc.id}`}
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{primary}</p>
        <p className="truncate text-xs text-muted-foreground">
          {primary !== doc.title ? `${doc.title} · ` : ''}
          {secondary}
        </p>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="hidden h-8 w-8 md:inline-flex"
        aria-label={t('documents.detail.download', 'Scarica')}
        onClick={(event) => {
          event.stopPropagation();
          onDownload(doc);
        }}
      >
        <Download className="h-4 w-4" />
      </Button>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
    </div>
  );
};
